import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { find, indexBy, keys, map, prop, sort } from 'rambda';
import { formatMonth } from '../../utils/dateHelpers';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const AccountUsers = ({ accounts, priorDates, priorDatesAsDates }) => {
	let navigate = useNavigate();
	function handleBack() {
		navigate('/reports', { replace: true });
	}

	return (
		<div className="py-4 px-4 sm:px-6 lg:px-8">
			<div className="pb-4" onClick={handleBack}>
				<span className="text-indigo-600">
					<ArrowUturnLeftIcon className="h-6 w-6 text-indigo-600" />
				</span>
			</div>
			<div className="sm:flex sm:items-center">
				<div className="sm:flex-auto">
					<h1 className="text-xl font-semibold text-gray-900">Active Accounts Usage Report</h1>
				</div>
			</div>
			<div className="mt-8 flex flex-col">
				<div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
					<div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
						<div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
							<table className="min-w-full divide-y divide-gray-300">
								<thead className="bg-gray-50">
									<tr>
										<th
											scope="col"
											className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
											ID
										</th>
										<th
											scope="col"
											className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Client
										</th>
										<th
											scope="col"
											className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Active
										</th>
										<th
											scope="col"
											className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Inactive
										</th>
										<th
											scope="col"
											className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Facilitator
										</th>
										<th
											scope="col"
											className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Max Users {formatMonth(priorDatesAsDates[0])}
										</th>
										<th
											scope="col"
											className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Max Users {formatMonth(priorDatesAsDates[1])}
										</th>
										<th
											scope="col"
											className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Max Users {formatMonth(priorDatesAsDates[2])}
										</th>
									</tr>
								</thead>
								<tbody className="bg-white">
									{accounts.map((acct, acctIdx) => (
										<tr key={acct.id} className={acctIdx % 2 === 0 ? undefined : 'bg-gray-50'}>
											<td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
												{acct.id}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												{acct.client_id}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												{acct.currentActiveUsers}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												{acct.currentInactiveUsers}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												{acct.currentFacilitatorUsers}
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												<div>
													{acct.priorCounts[priorDates[0]] && (
														<>
															<b>
																{acct.priorCounts[priorDates[0]].active || 0} Billable
																Total
															</b>
															<br />[{acct.priorCounts[priorDates[0]].manager || 0}{' '}
															Managers, &nbsp;
															{acct.priorCounts[priorDates[0]].individual || 0} Individual
															<br />
															{acct.priorCounts[priorDates[0]].facilitator || 0}{' '}
															Facilitator]
														</>
													)}
												</div>
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												<div>
													{acct.priorCounts[priorDates[1]] && (
														<>
															<b>
																{acct.priorCounts[priorDates[1]].active || 0} Billable
																Total
															</b>
															<br />[{acct.priorCounts[priorDates[1]].manager || 0}{' '}
															Managers, &nbsp;
															{acct.priorCounts[priorDates[1]].individual || 0} Individual
															<br />
															{acct.priorCounts[priorDates[1]].facilitator || 0}{' '}
															Facilitator]
														</>
													)}
												</div>
											</td>
											<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
												<div>
													{acct.priorCounts[priorDates[2]] && (
														<>
															<b>
																{acct.priorCounts[priorDates[2]].active || 0} Billable
																Total
															</b>
															<br />[{acct.priorCounts[priorDates[2]].manager || 0}{' '}
															Managers, &nbsp;
															{acct.priorCounts[priorDates[2]].individual || 0} Individual
															<br />
															{acct.priorCounts[priorDates[2]].facilitator || 0}{' '}
															Facilitator]
														</>
													)}
												</div>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const AccountUsersPage = () => {
	const [priorDates, setPriorDates] = useState(null);
	const [priorDatesAsDates, setPriorDatesAsDates] = useState(null);
	const [accounts, setAccounts] = useState(null);
	const [isLoaded, setIsLoaded] = useState(false);

	useEffect(() => {
		getCounts();
	}, []);

	if (isLoaded) {
		return <AccountUsers accounts={accounts} priorDates={priorDates} priorDatesAsDates={priorDatesAsDates} />;
	}

	function getCounts() {
		axios({
			method: 'GET',
			url: '/client/v1/monthlyCounts',
		}).then(
			response => {
				const counts = response.data;

				response.data.forEach(clientCount => {
					const countKeys = indexBy(obj => {
						const d = new Date(obj.dayOfMaxCount);
						return d.getTime();
					}, clientCount.maxActiveUsersPerMonthOverNumberOfMonths);
					clientCount.priorCounts = map(prop('maxActiveUsers'), countKeys);

					const priorKeys = keys(clientCount.priorCounts);
					priorKeys.forEach(k => {
						const fCount = clientCount.priorCounts[k].facilitator;
						let activeCount = clientCount.priorCounts[k].active - fCount;
						activeCount = activeCount - 1; // assume admin.user
						if (activeCount < 0) {
							activeCount = 0;
						}
						clientCount.priorCounts[k].active = activeCount;
					});

					if (clientCount.currentActiveUsers > 0) {
						clientCount.currentActiveUsers = clientCount.currentActiveUsers - 1; // for admin.user
					}
				});

				// Grab the dates from priorCounts array
				let firstFilledClientCount = find(item => {
					return keys(item.priorCounts).length === 3;
				}, counts);

				if (firstFilledClientCount) {
					const diff = (a, b) => {
						return b - a;
					};
					const thePriorDates = sort(diff, keys(firstFilledClientCount.priorCounts));

					const thePriorDatesAsDates = map(t => {
						const pd = new Date(parseInt(t, 10));
						pd.setDate(pd.getDate() + 1);
						return pd;
					}, thePriorDates);

					setPriorDates(thePriorDates);
					setPriorDatesAsDates(thePriorDatesAsDates);
				} else {
					setPriorDates([]);
					setPriorDatesAsDates([]);
				}

				setAccounts(counts);
				setIsLoaded(true);
			},
			error => {
				console.log(error);
			},
		);
	}
};
export default AccountUsersPage;
